<template>
  <transition :duration="300">
    <div class="common-model-up-outer" v-show="show">
      <transition name="common-model-up-mask">
        <div class="common-model-up-mask" />
      </transition>

      <transition name="common-model-up">
        <div
          class="common-model-up"
          @touchmove.prevent.stop
          @click.stop="onOverlayClick"
          v-show="show"
        >
          <div
            :class="{
              'common-model-up-container': true,
              'full-screen': fullScreen,
              'auto-height': autoHeight,
              'is-app': isApp,
              'is-ios': needSaftPadding && isIOS
            }"
            @click.stop
            @touchmove.stop
          >
            <div class="common-model-up-title" v-if="title">
              <span
                class="sub-btn"
                v-if="subBtnText"
                @click="$emit('subBtnClick')"
                >{{ subBtnText }}</span
              >
              <span v-html="title"></span>
            </div>
            <slot />
            <div
              class="common-model-up-btns"
              v-show="buttons.length || $slots.bottom"
            >
              <slot name="bottom" v-if="$slots.bottom"></slot>
              <div class="common-model-up-btns-container" v-else>
                <div
                  v-cloak
                  v-for="(item, index) in buttons"
                  :key="index"
                  :style="{
                    background: item.color
                  }"
                  :class="[
                    item.type && item.type === 'text-btn'
                      ? 'text-btn ' + themeClass.color
                      : 'common-model-up-btn ' +
                        (item.color ? '' : themeClass.bg),
                    item.disabled ? 'disabled' : '',
                    ...(item.className || [])
                  ]"
                  @click="handleButton(index)"
                >
                  <div class="common-model-up-btn-label">
                    {{ item.label }}
                  </div>
                  <div class="common-model-up-btn-desc" v-if="item.desc">
                    {{ item.desc }}
                  </div>
                </div>
              </div>
            </div>
            <div
              class="common-model-up-close-btn"
              @click="close"
              v-if="showCloseBtn"
            />
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
const themeInject =
  require('../theme/inject').default || require('../theme/inject')

const getPlatform = require('../../../tools/getPlatform.js').getPlatform
const stopScroll = require('../../../tools/domUtil.js').stopScroll
const recoverScroll = require('../../../tools/domUtil.js').recoverScroll

module.exports = {
  mixins: [themeInject],
  props: {
    animateName: {
      type: String,
      default: 'slide-up'
    },
    title: {
      type: String
    },
    show: {
      type: Boolean,
      default: false
    },
    buttons: {
      type: Array,
      default: () => []
    },
    subBtnText: {
      type: String
    },
    showCloseBtn: {
      type: Boolean,
      default: false
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    fullScreen: {
      type: Boolean,
      default() {
        return (this.$platform || this.platform || getPlatform()).isPc
      }
    },
    disableBodyScroll: {
      type: Boolean,
      default: false
    },
    closeOnClickOverlay: {
      type: Boolean,
      default: true
    },
    needSaftPadding: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    close() {
      this.$emit('close')
      this.$emit('update:show', false)
    },
    handleButton(index) {
      if (typeof this.buttons[index].onClick === 'function') {
        this.buttons[index].onClick(this.close)
      } else {
        this.close()
      }
    },
    onOverlayClick() {
      if (this.closeOnClickOverlay) {
        this.close()
      }
    }
  },
  computed: {
    _platform() {
      return this.$platform || this.platform || getPlatform()
    },
    isApp() {
      const { isYixinliApp, isZiXunApp, isZixunShiApp } = this._platform
      return isYixinliApp || isZiXunApp || isZixunShiApp
    },
    isIOS() {
      return this._platform.isiOS
    },
    _showCloseBtn() {
      if (this.fullScreen) {
        return true
      } else {
        return this.showCloseBtn
      }
    }
  },
  watch: {
    show(newVal) {
      if (!this.disableBodyScroll) return
      if (newVal) {
        stopScroll()
      } else {
        recoverScroll()
      }
    }
  },
  destroyed() {
    if (!this.disableBodyScroll) return
    recoverScroll()
  }
}
</script>

<style lang="scss" scope>
[v-cloak] {
  display: none;
}
</style>
<style lang="scss" scoped>
@import '../../../scss/functions';

.common-model-up-enter-active,
.common-model-up-leave-active {
  transition: transform 0.3s;
  transform: translate3d(0, 0, 0);
}
.common-model-up-enter,
.common-model-up-leave-to {
  transform: translate3d(0, 100%, 0);
}

.common-model-up-mask-enter-active,
.common-model-up-mask-leave-active {
  transition: opacity 0.2s;
  opacity: 1;
}
.common-model-mask-up-enter,
.common-model-mask-up-leave-to {
  transition: opacity 0.2s;
  opacity: 0;
}

.common-model-up {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  &-mask,
  & {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }
  &-mask {
    background: rgba(0, 0, 0, 0.5);
  }

  &-outer {
    position: fixed;
    left: 50%;
    margin-left: pxToRem(-375);
    width: pxToRem(750);
    top: 0;
    bottom: 0;
    z-index: 12; //$modelZIndex;
  }
  &-container {
    background: #ffffff;
    border-radius: pxToRem(30) pxToRem(30) 0px 0px;
    position: relative;
    max-height: calc(100vh - 2rem);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    &:not(.auto-height) {
      flex: 1;
    }
    &.full-screen {
      max-height: 100vh;
      &.is-app {
        max-height: calc(100vh - 1.173333rem);
      }
    }
    &.is-ios {
      padding-bottom: pxToRem(50);
    }
    &.auto-height {
      flex: unset;
      overflow-y: auto;
    }
  }
  &-close-btn {
    position: absolute;
    // padding: pxToRem(30.1) pxToRem(29.5);
    right: 0;
    top: 0;
    width: pxToRem(48);
    height: pxToRem(48);
    border-radius: pxToRem(48);
    background: #e9eef1;
    margin-top: pxToRem(20);
    margin-right: pxToRem(20);
    &:before,
    &:after {
      width: 50%;
      height: pxToRem(4);
      border-radius: pxToRem(4);
      content: '';
      background: #6c798c;
      position: absolute;
      left: 50%;
      top: 50%;
    }

    &:before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &:after {
      transform: translate(-50%, -50%) rotate(135deg);
    }
    // background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQEAYAAABPYyMiAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAgY0hSTQAAeiYAAICEAAD6AAAAgOgAAHUwAADqYAAAOpgAABdwnLpRPAAAAAZiS0dEAAAAAAAA+UO7fwAAAAlwSFlzAAAASAAAAEgARslrPgAAAgJJREFUSMfFk79r6lAYhr+jg9Lq4NBQurgKDu4OBR0K/TE6ZhAKLUKaFoTiH1HBRocOgktGB6EUXZqCtUMki4PkC+IQkVJRKqgFhaS5w+0R7i2isULPEjh8532eHM4L8NurzbSZNsMwWlyLa/G3N/ShD30XF5vm0FzKaXEtrsXt7Dg6vU6v03t/hwlMYIIIBShA4fYWeeSR5/kfg2nOVy7ldLPdbDc7HDoiJEIixDDGqXFqnDo6Ahe4wPX0BFGIQjSTWVcEwxjG8Pk5zYEBDGDw8mKAAQacnFDut4OKoiiKsrWFZSxj+fERS1jC0ufnqiIUPD+Xxzzmn5+bsWasGfN4/p8ni4KoiKfv6Xv69/cwgxnMIhGQQALp6iogBISAIAh0XlVVVVXPzohGNKLd3dE/NitmxawcHgaLwWKwOJmsLLCqiJWwElZiOrULXlmArkaykWwkt7fdult36w8PFmuxFru/Pw8SiUjEanXqn/qn/uPjUDqUDqU/Puy+naULRzjC0fU1IiKiZc2/X/t285wrg/95hDc3JEMyJFOtEolIRNJ18IIXvKenHMMxHDMc5uScnJNleVmuYyl4SZ0W1reOdaxfXq5/1TbrtLC+dkXsgjcm8lPwuiLkL2B318k6WSf7+rrpOi2qrymaoinu7c0H1ZpaU2sHB9R8XeCyG6Ecuv8HgsX8knx/jLYAAAAldEVYdGRhdGU6Y3JlYXRlADIwMTktMTEtMTNUMTc6MTU6NTUrMDg6MDDQTiMmAAAAJXRFWHRkYXRlOm1vZGlmeQAyMDE5LTExLTEzVDE3OjE1OjU1KzA4OjAwoRObmgAAAEl0RVh0c3ZnOmJhc2UtdXJpAGZpbGU6Ly8vaG9tZS9hZG1pbi9pY29uLWZvbnQvdG1wL2ljb25fenlsZnEyYnFyaWQvZ3VhbmJpLnN2Z7wGMJkAAAAASUVORK5CYII=)
    //   no-repeat center;

    // background-size: pxToRem(25.5) auto;
  }
  &-title {
    font-size: pxToRem(36);
    color: #21252d;
    text-align: center;
    line-height: pxToRem(36);
    // @include overLine();
    font-weight: bold;
    padding-top: pxToRem(60);
    position: relative;
    .sub-btn {
      position: absolute;
      font-size: pxToRem(30);
      color: #21252d;
      line-height: pxToRem(40);
      bottom: pxToRem(-5);
      font-weight: normal;
      left: pxToRem(40);
    }
  }
  &-btns {
    padding: 0 pxToRem(25) pxToRem(43);
    &-container {
      display: flex !important;
      width: 100% !important;
      align-items: center !important;
      flex-wrap: nowrap !important;
    }
  }
  &-btn {
    flex: 1;
    /*margin: 0 pxToRem(25);*/
    border-radius: pxToRem(80);
    height: pxToRem(80);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: #ffa202;
    opacity: 1;

    &-label {
      font-size: pxToRem(28);
      line-height: pxToRem(34);
    }
    &-desc {
      font-size: pxToRem(20);
      line-height: pxToRem(26);
    }
    &.disabled {
      opacity: 0.7;
    }
  }
  .text-btn {
    display: inline-block;
    color: #718aff;
    margin: 0 pxToRem(30);
    box-sizing: border-box;
  }
}
</style>
